import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import type { DeleteEntityTextElements } from "~/components/DeleteEntityView";
import DeleteEntityView from "~/components/DeleteEntityView";
import Label from "~/components/formElements/Label";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import {
	deleteStaffMemberBenefit,
} from "~/modules/humanResources/api/staffMemberBenefit/staffMemberBenefitApiDispatchers.ts";
import {
	STAFF_MEMBERS_BENEFITS_QUERY_KEY,
} from "~/modules/humanResources/api/staffMemberBenefit/staffMemberBenefitQueries.ts";
import type {
	StaffMemberBenefitWithDate,
} from "~/modules/humanResources/api/staffMemberBenefit/staffMemberBenefitTypes.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatDateWithGermanMonth } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	benefitData: StaffMemberBenefitWithDate | null
	onCloseClick: () => void;
	isOpen: boolean;
};

const DeleteBenefitEntryModal: React.FunctionComponent<Props> = ({
	benefitData,
	onCloseClick,
	isOpen,
}) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const queryClient = useQueryClient();


	useEffect(() => {
		if (isOpen) {
			setError("");
		}
	}, [isOpen]);

	const handleConfirmationClick = async () => {
		if (!benefitData) return;
		try {
			setBusy(true);
			await deleteStaffMemberBenefit(benefitData.staffMemberId, benefitData.id);
			await queryClient.refetchQueries({ queryKey: STAFF_MEMBERS_BENEFITS_QUERY_KEY(benefitData.staffMemberId) });

			onCloseClick();
		} catch (error) {
			const errorData = handleApiError(error);
			if (errorData.type === ApiResponseErrorType.VALIDATION) {
				if (
					errorData.messages.projectId.find(
						(validationError) => validationError.rule === "absenceIsDeletableOrUpdatableRule",
					)
				) {
					setError("Der Eintrag kann nicht gelöscht werden.");
				}
			} else {
				setError("Ein unbekannter Fehler ist aufgetreten.");
			}
		}
		setBusy(false);
	};

	const texts: DeleteEntityTextElements = {
		headerText: `FTE-Eintrag löschen`,
		bodyText: `Möchtest Du diesen Eintrag wirklich löschen?`,
		deleteButtonText: `Eintrag löschen`,
	};

	return (
		<DeleteEntityView
			key={isOpen ? "open" : "closed"}
			error={error}
			texts={texts}
			onCloseClick={onCloseClick}
			isOpen={isOpen}
			busy={busy}
			handleConfirmationClick={handleConfirmationClick}
		>
			{!!benefitData && <div className="mb-4 grid grid-cols-2 gap-y-2">
				<div className="col-span-2">
					<Label>Beschreibung</Label>
					<span className="text-base">{benefitData.description}</span>
				</div>
				<div className="col-span-2">
					<Label>Monatlicher Wert</Label>
					<span className="text-base">{formatNumberToCurrency(benefitData.amountCents)}</span>
				</div>
				<div>
					<Label>Startddatum</Label>
					<span className="text-base">{formatDateWithGermanMonth(benefitData.startDate)}</span>
				</div>
				<div>
					<Label>Enddatum</Label>
					<span className="text-base">{benefitData.endDate ? formatDateWithGermanMonth(benefitData.endDate) : EMPTY_VALUE_PLACEHOLDER}</span>
				</div>
			</div>}

		</DeleteEntityView>
	);
};

export default DeleteBenefitEntryModal;
